import { useCallback, useMemo, useState } from 'react'

import { useRouter } from 'next/navigation'

import { useDeviceInfo } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  Breakpoint,
  SavedSearchesDropdown,
  type GlobalHeaderProps,
} from '@b-stock/fe-core/bstock-react'

import HomeSavedSearchModal from '@/components/homeSavedSearchModule/HomeSavedSearchModal'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useSavedSearchesDropdownData } from './dropdownData/useSavedSearchesDropdownData'
import {
  useOpenDropdownToggle,
  type OpenDropdownState,
} from './useOpenDropdownState'

export function useSavedSearch({
  accountId,
  personId,
  isAuthenticated,
  openDropdownState,
}: {
  accountId: string | undefined
  personId: string | undefined
  isAuthenticated: boolean
  openDropdownState: OpenDropdownState
}) {
  const router = useRouter()
  const { trackButtonClicked } = useAnalytics()

  const enableSavedSearchesPage = useFlag(
    FeatureFlags.ENABLE_SAVED_SEARCHES_PAGE
  )

  const [isSavedSearchModalOpen, setIsSavedSearchModalOpen] = useState(false)

  const { items: savedSearchesItems } = useSavedSearchesDropdownData(
    accountId,
    personId
  )

  const savedSearchesItemsWithAnalytics = useMemo(
    () =>
      savedSearchesItems.map((item, index) => ({
        ...item,
        onClick: () => {
          item.onClick?.()
          trackButtonClicked({
            buttonName: 'global_header_saved_search_clicked',
            listing_position: index + 1,
            search_term: typeof item.label === 'string' ? item.label : '',
            results: item.resultsCount,
          })
        },
      })),
    [savedSearchesItems, trackButtonClicked]
  )

  const { onOpenChange, isOpen } = useOpenDropdownToggle({
    key: 'savedSearches',
    openDropdownState,
  })

  const handleSavedSearchesSeeAll = useCallback(() => {
    trackButtonClicked({
      buttonName: 'global_header_see_all_searches',
    })
    onOpenChange(false)
    const canNavigateToSavedSearchesPage = enableSavedSearchesPage && personId
    if (canNavigateToSavedSearchesPage) {
      router.push(`/buy/p/${personId}/saved-searches`)
    } else {
      setIsSavedSearchModalOpen(true)
    }
  }, [
    trackButtonClicked,
    enableSavedSearchesPage,
    personId,
    router,
    onOpenChange,
  ])

  const content = useMemo(
    () =>
      isAuthenticated ? (
        <SavedSearchesDropdown
          items={savedSearchesItemsWithAnalytics}
          onSeeAll={handleSavedSearchesSeeAll}
        />
      ) : null,
    [
      isAuthenticated,
      savedSearchesItemsWithAnalytics,
      handleSavedSearchesSeeAll,
    ]
  )

  const savedSearchesSlot = useMemo(
    () =>
      content
        ? {
            content,
            onOpenChange,
            isOpen,
          }
        : undefined,
    [content, onOpenChange, isOpen]
  )

  const { breakpoint } = useDeviceInfo()
  const isMobile = breakpoint < Breakpoint.MEDIUM

  return {
    headerProps: {
      savedSearchesUseNewPage: enableSavedSearchesPage,
      savedSearchesSlot,
    } satisfies Pick<
      GlobalHeaderProps,
      'savedSearchesSlot' | 'savedSearchesUseNewPage'
    >,
    modal: isSavedSearchModalOpen ? (
      <HomeSavedSearchModal
        closeModal={() => setIsSavedSearchModalOpen(false)}
        isMobile={isMobile}
      />
    ) : null,
  }
}
